import React from 'react';
import { ArrowDown, ArrowRight, ArrowUp, ArrowUpRight, Check, Hourglass, Minus, TriangleAlert } from 'lucide-react';
import Definition from '../../ui/Definition';
import { METRIC_DEFINITIONS } from '../../lib/glossary';
import { destinationLabel, type Destination, type KomTile, type StatusIcon } from '../../lib/kom';
import { windowShort } from '../../lib/funnel';

const STATUS_ICONS: Record<StatusIcon, typeof Check> = {
  check: Check,
  hourglass: Hourglass,
  alert: TriangleAlert,
  minus: Minus,
};

export function StatusGlyph({ icon }: { icon: StatusIcon }) {
  const Icon = STATUS_ICONS[icon];
  return <Icon size={16} strokeWidth={1.5} aria-hidden />;
}

const CHANGE_ICONS = { up: ArrowUp, down: ArrowDown, flat: Minus };
const CHANGE_WORDS = { up: 'Up', down: 'Down', flat: 'No change,' };

interface MetricTileProps {
  tile: KomTile;
  onOpen?: (destination: Destination) => void;
}

/**
 * One metric (docs/c3/DESIGN.md, Metric card). A tile that opens something is one button with an arrow;
 * a tile that does not is plain text, with no arrow and no hover (item 66).
 */
const MetricTile: React.FC<MetricTileProps> = ({ tile, onOpen }) => {
  const { destination } = tile;
  const opens = Boolean(destination && onOpen);
  const definition = METRIC_DEFINITIONS[tile.id];
  const ChangeIcon = tile.change ? CHANGE_ICONS[tile.change.direction] : null;
  const total = tile.severity?.reduce((sum, part) => sum + part.count, 0) ?? 0;

  return (
    <div
      className={`c3-kom-tile${opens ? ' is-link' : ''}${tile.attention ? ' is-attn' : ''}`}
      data-metric={tile.id}
    >
      <div className="c3-kom-head">
        {opens && destination ? (
          <button
            type="button"
            className="c3-kom-open"
            aria-label={`${tile.label}, ${destinationLabel(destination)}`}
            onClick={() => onOpen?.(destination)}
          >
            {tile.label}
          </button>
        ) : (
          <span className="c3-kom-label">{tile.label}</span>
        )}
        <Definition term={definition.term} text={definition.text} />
        <span className="c3-kom-tag">
          {tile.chip && (
            <span className={`c3-kom-chip is-${tile.chip.tone}`}>
              <StatusGlyph icon={tile.chip.icon} />
              {tile.chip.text}
            </span>
          )}
          <span>{tile.scope === 'now' ? 'Now' : windowShort(tile.scope)}</span>
          {opens && <ArrowUpRight className="c3-kom-arrow" size={16} strokeWidth={1.5} aria-hidden />}
        </span>
      </div>

      <div className={`c3-kom-figure${tile.value == null ? ' is-empty' : ''}${tile.alert ? ' is-alert' : ''}`}>
        {tile.value ?? '—'}
      </div>

      {tile.severity && total > 0 && (
        <div className="c3-kom-sevbar" aria-hidden>
          {tile.severity
            .filter((part) => part.count > 0)
            .map((part) => (
              <i key={part.id} className={`is-${part.id}`} style={{ flexGrow: part.count }} />
            ))}
        </div>
      )}

      <p className="c3-kom-detail" title={tile.detailTitle}>
        {tile.detail}
      </p>

      {tile.action && (
        <p className="c3-kom-action">
          {tile.action}
          <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
        </p>
      )}

      {tile.change && ChangeIcon && (
        <div className="c3-kom-foot">
          <ChangeIcon size={14} strokeWidth={1.5} aria-hidden />
          <span className="sr-only">{CHANGE_WORDS[tile.change.direction]} </span>
          <span>{tile.change.text}</span>
          <span className="c3-kom-vs">vs previous</span>
        </div>
      )}
    </div>
  );
};

export default MetricTile;
